import React, { useState } from 'react';
import { 
  Home, 
  ShoppingCart, 
  Package, 
  Users, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Newspaper,  // Icon for News
  MapPin,     // Icon for Coverage Area
  MessageCircle // Icon for Live Chat (baru ditambahkan)
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SidebarHeader = ({ activeTab, currentUser , onClose }) => { // Tambah onClose prop untuk mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    {
      id: 'overview',
      label: 'Dashboard',
      icon: Home,
      description: 'Overview & Analytics',
      path: '/dashboard-admin',
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCart,
      description: 'Manage Customer Orders',
      path: '/admin-order',
    },
    {
      id: 'packages',
      label: 'Packages',
      icon: Package,
      description: 'Internet Packages',
      path: '/admin-package',
    },
    {
      id: 'customers',
      label: 'Customers',
      icon: Users,
      description: 'Customer Management',
      path: '/admin-customer',
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: FileText,
      description: 'Invoices & Payments',
      path: '/admin-billing',
    },
    {
      id: 'chat', // Menu item baru untuk Live Chat
      label: 'Live Chat',
      icon: MessageCircle,
      description: 'Customer Support',
      path: '/adminchat',
    },
    {
      id: 'news',
      label: 'News',
      icon: Newspaper,
      description: 'Latest Updates',
      path: '/admin-news',
    },
    {
      id: 'coverage',
      label: 'Coverage Area',
      icon: MapPin,
      description: 'Service Areas',
      path: '/admin-coverage',
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'System Settings',
      path: '/settings',
    }
  ];

  const handleLogout = () => {
    // Hapus semua data autentikasi yang tersimpan di localStorage
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('userData');

    // Redirect ke halaman login admin
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (onClose) onClose(); // Panggil onClose jika dari parent (mobile)
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsSidebarOpen(false);
    if (onClose) onClose(); // Tutup sidebar di mobile setelah navigasi
  };

  return (
    <>
      {/* Mobile menu button - hanya tampil jika tidak ada parent toggle */}
      {!onClose && (
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <button
            onClick={toggleSidebar}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50"
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? (
              <X className="w-6 h-6 text-gray-600" />
            ) : (
              <Menu className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>
      )}

      {/* Overlay for mobile */}
      {isSidebarOpen && !onClose && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out
        flex flex-col h-screen overflow-hidden
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} aria-label="Sidebar navigation">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-20 h-20 flex items-center justify-center overflow-hidden">
              <img
                src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Telnet</h1>
            </div>
          </div>
          {/* Tombol close untuk mobile */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* User Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {currentUser ?.name ? currentUser .name.charAt(0) : ''}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{currentUser ?.name}</p>
              <p className="text-xs text-gray-500 truncate">{currentUser ?.role}</p>
            </div>
            <Bell className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200
                  ${isActive 
                    ? 'bg-green-50 text-green-700 border-r-2 border-green-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-green-700' : 'text-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${isActive ? 'text-green-700' : 'text-gray-900'}`}>
                    {item.label}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {item.description}
                  </div>
                </div>
                {item.id === 'orders' && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                    New
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors duration-200"
            aria-label="Logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>

        {/* Version Info */}
        <div className="p-4 text-center">
          <p className="text-xs text-gray-400 select-none">NetSpeed Admin v1.0.0</p>
        </div>
      </aside>
    </>
  );
};

export default SidebarHeader;